import React, { useState, useEffect, useCallback } from 'react';
import { automunityAPI } from '../lib/api';
import './Changelog.css';

interface ChangelogEntry {
  changelog_id: number;
  commit_id: string;
  commit_message: string;
  commit_author: string;
  commit_date: string;
  files_changed: any;
  automation_ids: any;
  automation_names: string;
  digest_description: string;
  repository: string;
  request_id?: number;
  request_title?: string;
  created_at: string;
}

interface ChangelogProps {
  automationId?: string; // If provided, filters to a specific automation
  embedded?: boolean;    // If true, renders without the page header
}

const Changelog: React.FC<ChangelogProps> = ({ automationId, embedded = false }) => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState<number | null>(null);
  const [limit, setLimit] = useState(50);
  const [repoFilter, setRepoFilter] = useState<string>('all');

  const fetchChangelog = useCallback(async () => {
    try {
      setLoading(true);
      const res = await automunityAPI.getChangelog(automationId, limit);
      const data = await res.body.json();
      const records = (data as any)?.records || (Array.isArray(data) ? data : []);
      setEntries(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Error fetching changelog:', err);
    } finally {
      setLoading(false);
    }
  }, [automationId, limit]);

  useEffect(() => {
    fetchChangelog();
  }, [fetchChangelog]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    // Handle git epoch format: "1783471466 -0400"
    const epochMatch = dateStr.match(/^(\d+)\s/);
    if (epochMatch) {
      return new Date(Number(epochMatch[1]) * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    const ts = Number(dateStr);
    if (!isNaN(ts) && ts > 1000000000) {
      return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    }
    return dateStr;
  };

  const parseFiles = (files: any): Array<{ path: string; change_type: string }> => {
    if (!files) return [];
    try {
      return typeof files === 'string' ? JSON.parse(files) : files;
    } catch { return []; }
  };

  if (loading) return <div className="changelog-loading">Loading changelog...</div>;

  return (
    <div className={`changelog-container ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <div className="changelog-header">
          <h1>Changelog</h1>
          <p className="changelog-subtitle">Recent development activity across all automations</p>
          <div className="changelog-filters">
            <select value={repoFilter} onChange={e => setRepoFilter(e.target.value)} className="repo-filter">
              <option value="all">All Repositories</option>
              {Array.from(new Set(entries.map(e => e.repository).filter(Boolean))).map(repo => (
                <option key={repo} value={repo}>{repo}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="changelog-empty">No changelog entries found.</div>
      ) : (
        <div className="changelog-timeline">
          {entries.filter(e => repoFilter === 'all' || e.repository === repoFilter).map(entry => (
            <div key={entry.changelog_id} className="changelog-entry">
              <div className="changelog-entry-header" onClick={() => setExpandedEntry(expandedEntry === entry.changelog_id ? null : entry.changelog_id)}>
                <div className="entry-left">
                  <span className="entry-dot" />
                  <div className="entry-content">
                    <div className="entry-digest">{entry.digest_description || entry.commit_message}</div>
                    <div className="entry-meta">
                      <span className="entry-author">{entry.commit_author}</span>
                      <span className="entry-date">{formatDate(entry.commit_date)}</span>
                      {entry.automation_names && !automationId && (
                        <span className="entry-automations">{entry.automation_names}</span>
                      )}
                      {entry.request_id && (
                        <span className="entry-request">REQ-{entry.request_id}</span>
                      )}
                      {entry.repository && (
                        <span className="entry-repo">{entry.repository}</span>
                      )}
                    </div>
                  </div>
                </div>
                <span className="entry-expand">{expandedEntry === entry.changelog_id ? '▾' : '▸'}</span>
              </div>

              {expandedEntry === entry.changelog_id && (
                <div className="changelog-entry-expanded">
                  <div className="entry-commit-msg">
                    <strong>Commit:</strong> <code>{entry.commit_id?.slice(0, 8)}</code>
                    <p>{entry.commit_message}</p>
                  </div>
                  {parseFiles(entry.files_changed).length > 0 && (
                    <div className="entry-files">
                      <strong>Files Changed ({parseFiles(entry.files_changed).length})</strong>
                      <ul>
                        {parseFiles(entry.files_changed).slice(0, 20).map((f, i) => (
                          <li key={i} className={`file-${f.change_type || 'M'}`}>
                            <span className="file-badge">{f.change_type || 'M'}</span>
                            {f.path}
                          </li>
                        ))}
                        {parseFiles(entry.files_changed).length > 20 && (
                          <li className="file-more">...and {parseFiles(entry.files_changed).length - 20} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length >= limit && (
        <button className="changelog-load-more" onClick={() => setLimit(limit + 50)}>
          Load More
        </button>
      )}
    </div>
  );
};

export default Changelog;
